import { Box, Icon, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { useCallback } from "react";
import { useGuardedNavigate } from "../navigation/useGuardedNavigate";
import { FiFileText } from "react-icons/fi";
import { bffApi, useResolveAgentDocumentSourcesMutation } from "../../autogen/bff-api";
import { urls } from "../../urls";
import { useAppDispatch } from "../redux/hooks";
import type { DocumentSource, DocumentSourceOwner } from "./agentTypes";

const OWNER_ROUTES: Record<DocumentSourceOwner["entityType"], (id: string) => string> = {
  CONTRACT: (id) => urls.contracts.viewSingle.go(id),
  SOURCING_EVENT_V2: (id) => urls.app.sourcingEvents.view.go(id),
};

const pagesLabel = (pages: number[]): string | null => (pages.length > 0 ? `s. ${pages.join(", ")}` : null);

// Source cards for the documents a turn's search grounded the answer in. Clicking the document
// re-resolves a fresh download link (the stored one is short-lived, and restored conversations
// would otherwise hold dead links); the owner line navigates to the contract/anskaffelse it
// belongs to.
export const DocumentSourceCards = ({ sources }: { sources: DocumentSource[] }) => {
  const navigate = useGuardedNavigate();
  const dispatch = useAppDispatch();
  const [resolveSources] = useResolveAgentDocumentSourcesMutation();

  const freshUrl = useCallback(
    async (source: DocumentSource): Promise<string | undefined> => {
      if (source.memoryId) {
        const resolved = await resolveSources({
          resolveAgentDocumentSourcesRequest: { memoryIds: [source.memoryId] },
        }).unwrap();
        return resolved.sources[0]?.downloadUrl;
      }
      if (!source.documentId) {
        return undefined;
      }
      const subscription = dispatch(
        bffApi.endpoints.getDownloadLink.initiate({ docId: source.documentId }, { forceRefetch: true }),
      );
      try {
        return (await subscription.unwrap()).url;
      } finally {
        subscription.unsubscribe();
      }
    },
    [resolveSources, dispatch],
  );

  const download = useCallback(
    async (source: DocumentSource) => {
      const opened = window.open("about:blank", "_blank");
      try {
        const url = await freshUrl(source);
        if (url && opened) {
          opened.location.href = url;
        } else {
          opened?.close();
        }
      } catch {
        opened?.close();
      }
    },
    [freshUrl],
  );

  return (
    <Wrap spacing="2">
      {sources.map((source) => {
        const owner = source.owner;
        const ownerRoute = owner ? OWNER_ROUTES[owner.entityType] : undefined;
        const pages = pagesLabel(source.pages);
        return (
          <WrapItem key={source.memoryId ?? source.documentId ?? source.documentName}>
            <Box
              px="3"
              py="2"
              borderWidth="1px"
              borderColor="smBorder"
              borderRadius="lg"
              bg="smBackground"
              minW="0"
            >
              <Box
                as="button"
                type="button"
                onClick={() => void download(source)}
                display="flex"
                alignItems="center"
                gap="2"
                textAlign="left"
                _hover={{ color: "smPrimary" }}
              >
                <Icon as={FiFileText} boxSize="4" color="smPrimary" flexShrink={0} />
                <Text fontSize="sm" fontWeight="semibold" color="smText" noOfLines={1}>
                  {source.documentName}
                  {pages && (
                    <Text as="span" fontWeight="normal" color="smTextDisabled">
                      {" "}
                      ({pages})
                    </Text>
                  )}
                </Text>
              </Box>
              {owner && ownerRoute && (
                <Text
                  as="button"
                  type="button"
                  onClick={() => navigate(ownerRoute(owner.entityId))}
                  fontSize="xs"
                  color="smTextDisabled"
                  noOfLines={1}
                  display="block"
                  textAlign="left"
                  _hover={{ color: "smPrimary", textDecoration: "underline" }}
                >
                  {owner.entityTitle}
                </Text>
              )}
            </Box>
          </WrapItem>
        );
      })}
    </Wrap>
  );
};
